import { useEffect, useRef, useState, useCallback } from "react";
import Seo from '../components/Seo';
import { motion } from "framer-motion";
import { FiSend, FiZap, FiBarChart2, FiUsers, FiServer, FiStar, FiActivity, FiShield, FiSettings, FiAlertTriangle, FiGlobe, FiCheck, FiX, FiRefreshCw, FiLock, FiUnlock, FiCheckCircle, FiInfo, FiArrowRight } from "react-icons/fi";

// Компоненты для карты и статистики (остаются без изменений)
const generateColorFromString = (str) => {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	const hue = hash % 360;
	return `hsl(${hue}, 70%, 65%)`;
};

// Компонент карты атак (без изменений)
const AttackMap = () => {
	const canvasRef = useRef(null);
	const attacksRef = useRef([]);
	const attackQueueRef = useRef([]);
	const lastAttackTimeRef = useRef(0);
	const mapImageRef = useRef(null);

	const TRAIL_LENGTH = 0.2;
	const CIRCLE_DURATION = 2000;
	const ATTACK_DELAY = 100;
	const ATTACK_SPEED = 0.3;

	const attackColors = {
		1: '#ff4444',
		2: '#4444ff',
		3: '#44ff44',
		4: '#ffff44',
		5: '#ff44ff',
		6: '#44ffff'
	};

	const projectToImage = useCallback((lat, lng, imgW, imgH) => {
		const x = ((lng + 180) / 360) * imgW;
		const y = ((90 - lat) / 180) * imgH;
		return { x, y };
	}, []);

	useEffect(() => {
		const img = new Image();
		img.src = '/map.svg';
		img.onload = () => {
			mapImageRef.current = {
				width: img.naturalWidth || img.width || 2048,
				height: img.naturalHeight || img.height || 1024
			};
		};
		img.onerror = () => {
			mapImageRef.current = { width: 2048, height: 1024 };
		};
	}, []);

	const fetchAttacks = useCallback(async () => {
		try {
			const response = await fetch('/api/attack-map');
			const data = await response.json();
			const newAttacks = data.map(attack => ({
				type: parseInt(attack[0]),
				srcLat: parseFloat(attack[1]),
				srcLng: parseFloat(attack[2]),
				destLat: parseFloat(attack[3]),
				destLng: parseFloat(attack[4]),
				timestamp: parseInt(attack[5])
			}));
			attackQueueRef.current = [...attackQueueRef.current, ...newAttacks];
		} catch (error) {
			console.error('Error fetching attack data:', error);
		}
	}, []);

	useEffect(() => {
		fetchAttacks();
		const interval = setInterval(fetchAttacks, 60000);
		return () => clearInterval(interval);
	}, [fetchAttacks]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext('2d');
		let animationId;

		const resizeCanvas = () => {
			const dpr = window.devicePixelRatio || 1;
			const clientW = canvas.clientWidth;
			const clientH = canvas.clientHeight;
			canvas.width = Math.round(clientW * dpr);
			canvas.height = Math.round(clientH * dpr);
			canvas.style.width = clientW + 'px';
			canvas.style.height = clientH + 'px';
			ctx.setTransform(1, 0, 0, 1, 0, 0);
			ctx.scale(dpr, dpr);
		};

		resizeCanvas();
		window.addEventListener('resize', resizeCanvas);

		const render = (currentTime) => {
			if (!canvas) return;
			const width = canvas.clientWidth;
			const height = canvas.clientHeight;

			ctx.clearRect(0, 0, width, height);
			ctx.fillStyle = 'rgba(0,0,0,0.06)';
			ctx.fillRect(0, 0, width, height);

			const imgInfo = mapImageRef.current;
			if (!imgInfo) {
				animationId = requestAnimationFrame(render);
				return;
			}

			const imgW = imgInfo.width;
			const imgH = imgInfo.height;

			const scale = Math.min(width / imgW, height / imgH);
			const drawW = imgW * scale;
			const drawH = imgH * scale;
			const offsetX = (width - drawW) / 2;
			const offsetY = (height - drawH) / 2;

			if (attackQueueRef.current.length > 0) {
				if (!lastAttackTimeRef.current || currentTime - lastAttackTimeRef.current >= ATTACK_DELAY) {
					const nextAttack = attackQueueRef.current.shift();
					attacksRef.current.push({
						...nextAttack,
						startTime: currentTime,
						progress: 0,
						circleProgress: 0,
						circleStartTime: null
					});
					lastAttackTimeRef.current = currentTime;
				}
			}

			const now = currentTime;

			const updatedAttacks = attacksRef.current.map(attack => {
				const srcImg = projectToImage(attack.srcLat, attack.srcLng, imgW, imgH);
				const destImg = projectToImage(attack.destLat, attack.destLng, imgW, imgH);

				const src = {
					x: offsetX + srcImg.x * scale,
					y: offsetY + srcImg.y * scale
				};
				const dest = {
					x: offsetX + destImg.x * scale,
					y: offsetY + destImg.y * scale
				};

				const dx = dest.x - src.x;
				const dy = dest.y - src.y;
				const distance = Math.sqrt(dx * dx + dy * dy);

				const elapsed = now - attack.startTime;
				const totalDuration = distance / ATTACK_SPEED;
				attack.progress = Math.min(elapsed / totalDuration, 1);

				if (attack.progress >= 1 && attack.circleStartTime === null) {
					attack.circleStartTime = now;
				}

				if (attack.circleStartTime !== null) {
					const circleElapsed = now - attack.circleStartTime;
					attack.circleProgress = Math.min(circleElapsed / CIRCLE_DURATION, 1);
				}

				if (attack.progress < 1) {
					const headProgress = attack.progress;
					const headX = src.x + dx * headProgress;
					const headY = src.y + dy * headProgress;

					const tailProgress = Math.max(0, headProgress - TRAIL_LENGTH);
					const tailX = src.x + dx * tailProgress;
					const tailY = src.y + dy * tailProgress;

					const grad = ctx.createLinearGradient(tailX, tailY, headX, headY);
					const color = attackColors[attack.type] || '#ffffff';
					grad.addColorStop(0, 'rgba(0,0,0,0)');
					grad.addColorStop(0.6, color);
					grad.addColorStop(1, color);

					ctx.beginPath();
					ctx.moveTo(tailX, tailY);
					ctx.lineTo(headX, headY);
					ctx.strokeStyle = grad;
					ctx.lineWidth = 2;
					ctx.stroke();
				}

				if (attack.circleProgress > 0 && attack.circleProgress < 1) {
					const circleRadius = attack.circleProgress * 30;
					const circleAlpha = 1 - attack.circleProgress;
					const color = attackColors[attack.type] || '#ffffff';

					const hex = color.replace('#', '');
					const r = parseInt(hex.substring(0, 2), 16);
					const g = parseInt(hex.substring(2, 4), 16);
					const b = parseInt(hex.substring(4, 6), 16);

					ctx.beginPath();
					ctx.arc(dest.x, dest.y, circleRadius, 0, Math.PI * 2);
					ctx.strokeStyle = `rgba(${r},${g},${b},${circleAlpha})`;
					ctx.lineWidth = 2;
					ctx.stroke();
				}

				return attack;
			}).filter(a => a.progress < 1 || a.circleProgress < 1);

			attacksRef.current = updatedAttacks;
			animationId = requestAnimationFrame(render);
		};

		animationId = requestAnimationFrame(render);

		return () => {
			window.removeEventListener('resize', resizeCanvas);
			cancelAnimationFrame(animationId);
		};
	}, [projectToImage, fetchAttacks]);

	return (
		<div style={{ position: 'relative', width: '100%', height: '100%' }}>
			<canvas
				ref={canvasRef}
				style={{ width: '95%', height: '100%', display: 'block' }}
			/>
		</div>
	);
};

// Компоненты статистики DDoS атак (без изменений)
const DDoSDetails = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await fetch('/api/ddos-detail');
				if (!response.ok) {
					throw new Error('Ошибка при загрузке данных');
				}
				const result = await response.json();
				setData(result);
			} catch (err) {
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
		const intervalId = setInterval(fetchData, 60000);
		return () => clearInterval(intervalId);
	}, []);

	if (loading) {
		return (
			<div className="h-full flex items-center justify-center">
				<p className="text-gray-400">Загрузка данных...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="h-full flex items-center justify-center">
				<p className="text-red-400">Ошибка: {error}</p>
			</div>
		);
	}

	return (
		<div className="h-full flex flex-col">
			<div className="flex-1 overflow-y-auto pr-2">
				<div className="space-y-4">
					{data.map((item) => (
						<div key={item.name} className="space-y-2">
							<div className="flex justify-between items-center">
								<div className="flex items-center">
									<div
										className="w-3 h-3 rounded-full mr-2"
										style={{ backgroundColor: generateColorFromString(item.name) }}
									></div>
									<span className="text-sm text-gray-400">{item.name}</span>
								</div>
								<span className="text-sm text-white">
									{item.value.toLocaleString()}%
								</span>
							</div>
							<div className="w-full bg-gray-700 rounded-full h-2">
								<div
									className="h-2 rounded-full transition-all duration-500"
									style={{
										width: `${item.value}%`,
										backgroundColor: generateColorFromString(item.name)
									}}
								></div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

const WebsiteAttacksDetails = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await fetch('/api/violation-detail');
				if (!response.ok) throw new Error('Ошибка при загрузке данных');
				const result = await response.json();
				setData(result);
			} catch (err) {
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
		const intervalId = setInterval(fetchData, 60000);
		return () => clearInterval(intervalId);
	}, []);

	if (loading) return <div className="h-full flex items-center justify-center">Загрузка данных...</div>;
	if (error) return <div className="h-full flex items-center justify-center text-red-400">Ошибка: {error}</div>;

	return (
		<div className="h-full flex flex-col">
			<div className="flex-1 overflow-y-auto pr-2">
				<div className="space-y-4">
					{data.map((item) => (
						<div key={item.name} className="space-y-2">
							<div className="flex justify-between items-center">
								<div className="flex items-center">
									<div
										className="w-3 h-3 rounded-full mr-2"
										style={{ backgroundColor: generateColorFromString(item.name) }}
									></div>
									<span className="text-sm text-gray-400">{item.name}</span>
								</div>
								<span className="text-sm text-white">
									{item.value.toLocaleString()}%
								</span>
							</div>
							<div className="w-full bg-gray-700 rounded-full h-2">
								<div
									className="h-2 rounded-full transition-all duration-500"
									style={{
										width: `${item.value}%`,
										backgroundColor: generateColorFromString(item.name)
									}}
								></div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

const GlobalStats = () => {
	const [stats, setStats] = useState({
		attacksBlocked: "2.1M+",
		activeThreats: "45K+",
		protectedSites: "15K+",
		responseTime: "12ms"
	});

	return (
		<div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
			<div className="text-center">
				<div className="text-3xl font-bold text-blue-400">{stats.attacksBlocked}</div>
				<div className="text-gray-400">Атак заблокировано</div>
			</div>
			<div className="text-center">
				<div className="text-3xl font-bold text-red-400">{stats.activeThreats}</div>
				<div className="text-gray-400">Активных угроз</div>
			</div>
			<div className="text-center">
				<div className="text-3xl font-bold text-green-400">{stats.protectedSites}</div>
				<div className="text-gray-400">Защищенных сайтов</div>
			</div>
			<div className="text-center">
				<div className="text-3xl font-bold text-purple-400">{stats.responseTime}</div>
				<div className="text-gray-400">Среднее время ответа</div>
			</div>
		</div>
	);
};

// Упрощенный компонент демонстрации дашборда
const DashboardDemo = ({ serviceName }) => {
	return (
		<div className="w-full max-w-6xl mx-auto">
			<div className="bg-gray-900/80 backdrop-blur-lg rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
				{/* Заголовок дашборда */}
				<div className="border-b border-white/10 p-6">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-2xl font-bold">Панель управления {serviceName}</h2>
						</div>
					</div>
				</div>

				{/* Содержимое дашборда */}
				<div className="p-6">
					{/* Основной скриншот */}
					<div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-gray-800 to-gray-900">
						{/* Десктопная версия */}
						<img
							src="/dashboard-screenshot-desktop.png"
							alt="Скриншот панели управления AI-CDN Protection"
							className="hidden md:block w-full h-auto"
						/>

						{/* Мобильная версия */}
						<img
							src="/dashboard-screenshot-mobile.png"
							alt="Скриншот панели управления AI-CDN Protection"
							className="md:hidden w-full h-auto"
						/>
						{/* Наложение с кнопкой для призыва к действию */}
						<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-8">
							<a
								href="/dashboard"
								className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-pink-500 rounded-xl font-semibold flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
							>
								<FiArrowRight className="w-5 h-5" />
								Попробовать бесплатно
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const DashboardDemoSection = ({ serviceName }) => {
	return (
		<section className="relative z-10 py-20 bg-gradient-to-b from-black/80 to-gray-900/80">
			<div className="max-w-7xl mx-auto px-4">
				<motion.div
					className="text-center mb-12"
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<h2 className="text-4xl md:text-5xl font-bold mb-4">
						Мощный и интуитивный интерфейс
					</h2>
					<p className="text-xl text-gray-300 max-w-3xl mx-auto">
						Полный контроль над защитой вашего сайта через удобную панель управления
						с аналитикой в реальном времени
					</p>
				</motion.div>

				<div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl p-8 border border-white/10">
					<DashboardDemo serviceName={serviceName} />

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
						<div className="text-center p-6 bg-white/5 rounded-2xl">
							<div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
								<FiActivity className="w-6 h-6 text-blue-400" />
							</div>
							<h4 className="font-semibold mb-2">Мониторинг в реальном времени</h4>
							<p className="text-gray-400 text-sm">
								Графики трафика, метрики производительности и статистика атак
							</p>
						</div>

						<div className="text-center p-6 bg-white/5 rounded-2xl">
							<div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
								<FiShield className="w-6 h-6 text-red-400" />
							</div>
							<h4 className="font-semibold mb-2">Управление защитой</h4>
							<p className="text-gray-400 text-sm">
								Настройка правил, белых/черных списков и уровней чувствительности
							</p>
						</div>

						<div className="text-center p-6 bg-white/5 rounded-2xl">
							<div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
								<FiSettings className="w-6 h-6 text-green-400" />
							</div>
							<h4 className="font-semibold mb-2">Детальная аналитика</h4>
							<p className="text-gray-400 text-sm">
								Подробные отчеты по трафику, угрозам и производительности
							</p>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

const PricingPlansSection = ({ plans, isLoading }) => {
	const [domains, setDomains] = useState(1);

	if (isLoading) {
		return (
			<section className="relative z-10 py-20 bg-gradient-to-b from-gray-900/90 to-black/90">
				<div className="max-w-7xl mx-auto px-4">
					<div className="text-center mb-16">
						<h2 className="text-4xl md:text-5xl font-bold mb-4">
							Выберите тариф
						</h2>
						<p className="text-xl text-gray-300 max-w-3xl mx-auto">
							Подходящее решение для проектов любого масштаба с возможностью
							бесплатного тестирования в течение 7 дней
						</p>
					</div>

					<div className="flex justify-center">
						<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				</div>
			</section>
		);
	}

	const calculatePrice = (planName, count) => {
		switch (planName) {
			case "Base":
				return count === 1
					? 999
					: 999 + (count - 1) * 900;

			case "Pro":
				return count < 5
					? count * 1249
					: 4499 + (count - 5) * 900;

			case "Full":
				return count < 10
					? count * 1500
					: 9499 + (count - 10) * 950;

			default:
				return null;
		}
	};

	const getRegularPrice = (planName, count) => {
		switch (planName) {
			case "Base":
				return count * 999;

			case "Pro":
				return count * 1249;

			case "Full":
				return count * 1500;

			default:
				return 0;
		}
	};

	const formatPrice = (price) =>
		Math.round(price).toLocaleString("ru-RU");

	const handleDomainsChange = (value) => {
		const count = parseInt(value, 10);

		if (Number.isNaN(count)) {
			setDomains(1);
			return;
		}

		setDomains(Math.min(100, Math.max(1, count)));
	};

	const basePrice = calculatePrice("Base", domains);
	const proPrice = calculatePrice("Pro", domains);
	const fullPrice = calculatePrice("Full", domains);

	const getDiscount = (planName, price) => {
		const regularPrice = getRegularPrice(planName, domains);

		if (regularPrice <= price) {
			return null;
		}

		return {
			amount: regularPrice - price,
			percent: Math.round(
				((regularPrice - price) / regularPrice) * 100
			),
		};
	};

	return (
		<section
			id="pricing"
			className="relative z-10 py-20 bg-gradient-to-b from-gray-900/90 to-black/90"
		>
			<div className="max-w-7xl mx-auto px-4">

				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<h2 className="text-4xl md:text-5xl font-bold mb-4">
						Выберите тариф
					</h2>

					<p className="text-xl text-gray-300 max-w-3xl mx-auto">
						Подходящее решение для проектов любого масштаба с возможностью
						бесплатного тестирования в течение 7 дней
					</p>
				</motion.div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
					{plans.map((plan, index) => (
						<motion.div
							key={index}
							className={`relative bg-white/5 backdrop-blur-lg rounded-3xl p-8 border transition-all duration-300 hover:scale-105 ${
								plan.popular
									? "border-purple-500/50 shadow-2xl shadow-purple-500/20"
									: "border-white/10"
							}`}
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: index * 0.2, duration: 0.6 }}
						>
							{plan.popular && (
								<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
									<span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
										<FiStar size={12} />
										Популярный
									</span>
								</div>
							)}

							{plan.beta && !plan.popular && (
								<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
									<span className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
										<FiZap size={12} />
										Beta
									</span>
								</div>
							)}

							<div className="text-center mb-8">
								<h3 className="text-2xl font-bold mb-2">
									{plan.name}
								</h3>

								<div className="text-4xl font-extrabold mb-2">
									{plan.price}₽/мес
								</div>

								<p className="text-gray-400">
									{plan.description}
								</p>
							</div>

							<ul className="space-y-4 mb-8">
								{plan.features.map((feature, idx) => (
									<li
										key={idx}
										className="flex items-center gap-3"
									>
										<FiCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
										<span>{feature}</span>
									</li>
								))}
							</ul>

							<a
								href="/dashboard"
								className={`w-full py-4 rounded-2xl font-semibold text-center block transition-all duration-300 ${
									plan.popular
										? "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-pink-500 hover:to-purple-500"
										: "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-purple-500 hover:to-blue-500"
								}`}
							>
								{plan.name === "Базовый"
									? "Начать"
									: "Попробовать"}
							</a>
						</motion.div>
					))}
				</div>

				<motion.div
					className="mt-16 max-w-5xl mx-auto"
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ delay: 0.3, duration: 0.6 }}
				>
					<div className="bg-gradient-to-r from-blue-900/20 via-purple-900/20 to-pink-900/20 backdrop-blur-lg rounded-3xl p-8 md:p-10 border border-white/10">

						{/* Заголовок */}
						<div className="text-center mb-8">
							<div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500">
								<FiGlobe className="w-6 h-6 text-white" />
							</div>

							<h3 className="text-2xl md:text-3xl font-bold mb-3">
								Рассчитайте стоимость
							</h3>

							<p className="text-gray-300 max-w-2xl mx-auto">
								Укажите количество доменов — мы автоматически
								рассчитаем стоимость каждого тарифа и покажем
								вашу экономию.
							</p>
						</div>

						{/* Выбор количества доменов */}
						<div className="max-w-xl mx-auto mb-10">
							<label className="block text-center text-sm text-gray-400 mb-3">
								Количество доменов
							</label>

							<div className="flex items-center gap-3">
								<button
									type="button"
									onClick={() =>
										setDomains((value) =>
											Math.max(1, value - 1)
										)
									}
									disabled={domains <= 1}
									className="w-12 h-12 flex-shrink-0 rounded-xl bg-white/10 border border-white/10 text-2xl hover:bg-white/20 transition disabled:opacity-30"
								>
									−
								</button>

								<input
									type="number"
									min="1"
									max="100"
									value={domains}
									onChange={(e) =>
										handleDomainsChange(e.target.value)
									}
									className="no-spinner w-full h-12 rounded-xl bg-black/30 border border-white/10 text-white text-center text-xl font-bold focus:outline-none focus:border-blue-500"
								/>

								<button
									type="button"
									onClick={() =>
										setDomains((value) =>
											Math.min(100, value + 1)
										)
									}
									disabled={domains >= 100}
									className="w-12 h-12 flex-shrink-0 rounded-xl bg-white/10 border border-white/10 text-2xl hover:bg-white/20 transition disabled:opacity-30"
								>
									+
								</button>
							</div>

							{/* Быстрый выбор */}
							<div className="flex flex-wrap justify-center gap-2 mt-4">
								{[1, 2, 5, 10, 20, 40, 50].map((count) => (
									<button
										key={count}
										type="button"
										onClick={() => setDomains(count)}
										className={`px-4 py-2 rounded-xl text-sm transition ${
											domains === count
												? "bg-blue-500 text-white"
												: "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
										}`}
									>
										{count}
									</button>
								))}
							</div>
						</div>

						{/* Результаты расчёта */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-5">

							{[
								{
									name: "Base",
									price: basePrice,
									color: "blue",
								},
								{
									name: "Pro",
									price: proPrice,
									color: "purple",
								},
								{
									name: "Full",
									price: fullPrice,
									color: "pink",
								},
							].map((item) => {
								const discount = getDiscount(
									item.name,
									item.price
								);

								return (
									<div
										key={item.name}
										className="rounded-2xl bg-black/20 border border-white/10 p-6"
									>
										<div className="flex items-center justify-between mb-4">
											<h4 className="text-xl font-bold">
												{item.name}
											</h4>

											{discount && (
												<span className="px-2.5 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-semibold">
													−{discount.percent}%
												</span>
											)}
										</div>

										<div className="mb-1">
											<span className="text-3xl font-extrabold">
												{formatPrice(item.price)}
											</span>

											<span className="text-gray-400 ml-1">
												₽/мес
											</span>
										</div>

										<div className="text-sm text-gray-400 mb-4">
											{formatPrice(item.price / domains)} ₽ за домен
										</div>

										{discount ? (
											<div className="rounded-xl bg-green-500/10 border border-green-500/10 p-3">
												<div className="text-sm text-green-400 font-medium">
													Экономия{" "}
													{formatPrice(
														discount.amount
													)}{" "}
													₽/мес
												</div>

												<div className="text-xs text-gray-500 mt-1">
													Без скидки:{" "}
													<span className="line-through">
														{formatPrice(
															getRegularPrice(
																item.name,
																domains
															)
														)}{" "}
														₽
													</span>
												</div>
											</div>
										) : (
											<div className="rounded-xl bg-white/5 p-3 text-sm text-gray-400">
												Стандартная стоимость
											</div>
										)}
									</div>
								);
							})}
						</div>

						{/* Пояснение */}
						<div className="mt-8 text-center text-sm text-gray-500">
							Стоимость указана за месяц. Чем больше доменов,
							тем ниже стоимость защиты одного домена.
						</div>
					</div>
				</motion.div>

				{/* Индивидуальный тариф */}
				<motion.div
					className="mt-8 max-w-4xl mx-auto"
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ delay: 0.4, duration: 0.6 }}
				>
					<div className="bg-gradient-to-r from-blue-900/20 via-purple-900/20 to-pink-900/20 backdrop-blur-lg rounded-3xl p-8 border border-white/10">

						<div className="flex flex-col lg:flex-row items-center justify-between gap-8">

							<div className="text-center lg:text-left">
								<div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
									<div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
										<FiSettings className="w-6 h-6 text-white" />
									</div>

									<h3 className="text-2xl font-bold">
										Индивидуальный тариф
									</h3>
								</div>

								<p className="text-gray-300 mb-4 max-w-2xl">
									Не нашли подходящий вариант? Мы готовы
									разработать индивидуальное решение,
									идеально соответствующее вашим потребностям
									и бюджету.
								</p>

								<div className="flex flex-wrap gap-2 justify-center lg:justify-start">
									<span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
										Корпоративные клиенты
									</span>

									<span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
										Сложные проекты
									</span>

									<span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">
										Гибкая настройка
									</span>
								</div>
							</div>

							<div className="flex-shrink-0">
								<a
									href="https://t.me/Shield_AI"
									target="_blank"
									rel="noopener noreferrer"
									className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-purple-600 hover:to-pink-600 rounded-2xl font-semibold flex items-center gap-3 transition-all duration-300 hover:scale-105 overflow-hidden"
								>
									<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

									<FiSend className="w-5 h-5 group-hover:translate-x-1 transition-transform" />

									Обсудить индивидуальный тариф
								</a>

								<p className="text-gray-400 text-sm mt-3 text-center">
									Бесплатная консультация
								</p>
							</div>
						</div>

						<div className="mt-8 pt-8 border-t border-white/10">
							<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
								<div className="text-center">
									<div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
										<FiUsers className="w-5 h-5 text-blue-400" />
									</div>

									<h4 className="font-semibold mb-1">
										Персональный менеджер
									</h4>

									<p className="text-gray-400 text-sm">
										Единый контакт для решения всех вопросов
									</p>
								</div>

								<div className="text-center">
									<div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
										<FiCheckCircle className="w-5 h-5 text-green-400" />
									</div>

									<h4 className="font-semibold mb-1">
										Точная настройка
									</h4>

									<p className="text-gray-400 text-sm">
										Только нужные функции без переплаты
									</p>
								</div>

								<div className="text-center">
									<div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
										<FiBarChart2 className="w-5 h-5 text-purple-400" />
									</div>

									<h4 className="font-semibold mb-1">
										Анализ требований
									</h4>

									<p className="text-gray-400 text-sm">
										Подробный аудит вашего проекта
									</p>
								</div>
							</div>
						</div>

					</div>
				</motion.div>
			</div>
		</section>
	);
};

export default function Home({ serviceName }) {
	const canvasRef = useRef(null);
	const animationFrameId = useRef(null);
	const [pricingPlans, setPricingPlans] = useState([]);
	const [isLoadingPlans, setIsLoadingPlans] = useState(true);

	useEffect(() => {
		async function fetchPricingPlans() {
			try {
				const response = await fetch('/api/ui/rates');
				if (response.ok) {
					const data = await response.json();
					setPricingPlans(data);
				} else {
					// Fallback на локальные данные если API недоступен
					setPricingPlans([
						{
							name: "Base",
							price: 999,
							description: "Базовая защита",
							features: [
								"Защита от ботов",
								"Ускорение сайта",
								"SSL-шифрование трафика",
								"Базовая аналитика",
								"1 домен",
							],
							popular: false
						},
						{
							name: "Pro",
							price: 4499,
							description: "Продвинутая защита",
							features: [
								"Защита от ботов",
								"DDoS защита",
								"SSL-шифрование трафика",
								"Расширенная аналитика",
								"Управление IP/странами",
								"5 доменов",
							],
							popular: true
						},
						{
							name: "Full",
							price: 9499,
							description: "Полная защита",
							features: [
								"AI защита + все функции Pro",
								"Полная аналитика",
								"10 доменов",
							],
							popular: false,
							beta: true
						}
					]);
				}
			} catch (error) {
				console.error('Ошибка загрузки тарифов:', error);
				// Fallback на локальные данные
				setPricingPlans([
					{
						name: "Base",
						price: 999,
						description: "Базовая защита",
						features: [
							"Защита от ботов",
							"Ускорение сайта",
							"SSL-шифрование трафика",
							"Базовая аналитика",
							"1 домен"
						],
						popular: false
					},
					{
						name: "Pro",
						price: 4499,
						description: "Продвинутая защита",
						features: [
							"Защита от ботов",
							"DDoS защита",
							"SSL-шифрование трафика",
							"Расширенная аналитика",
							"Управление IP/странами",
							"5 доменов",
						],
						popular: true
					},
					{
						name: "Full",
						price: 9499,
						description: "Полная защита",
						features: [
							"AI защита + все функции Pro",
							"Полная аналитика",
							"10 доменов",
						],
						popular: false,
						beta: true
					}
				]);
			} finally {
				setIsLoadingPlans(false);
			}
		}

		fetchPricingPlans();
	}, []);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		function setCanvasSize() {
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;
		}
		setCanvasSize();

		const particles = Array.from({ length: 150 }).map(() => ({
			x: Math.random() * canvas.width,
			y: Math.random() * canvas.height,
			dx: (Math.random() - 0.5) * 1.2,
			dy: (Math.random() - 0.5) * 1.2,
			size: Math.random() * 3 + 1,
			alpha: Math.random() * 0.5 + 0.1
		}));

		function animate() {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			for (let i = 0; i < particles.length; i++) {
				for (let j = i + 1; j < particles.length; j++) {
					const dx = particles[i].x - particles[j].x;
					const dy = particles[i].y - particles[j].y;
					const distance = Math.sqrt(dx * dx + dy * dy);

					if (distance < 120) {
						ctx.strokeStyle = `rgba(100, 100, 255, ${0.2 - distance / 600})`;
						ctx.lineWidth = 0.8;
						ctx.beginPath();
						ctx.moveTo(particles[i].x, particles[i].y);
						ctx.lineTo(particles[j].x, particles[j].y);
						ctx.stroke();
					}
				}
			}

			particles.forEach((p) => {
				p.x += p.dx;
				p.y += p.dy;

				if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
				if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

				ctx.fillStyle = `rgba(100, 150, 255, ${p.alpha})`;
				ctx.beginPath();
				ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
				ctx.fill();
			});

			animationFrameId.current = requestAnimationFrame(animate);
		}
		animate();

		window.addEventListener("resize", setCanvasSize);
		return () => {
			window.removeEventListener("resize", setCanvasSize);
			if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
		};
	}, []);

	const features = [
		{
			icon: <FiShield className="w-8 h-8" />,
			title: "Многоуровневая защита",
			description: "Защита от DDoS, ботов, сканеров и сложных кибератак с помощью AI",
			items: ["DDoS защита", "WAF фильтрация", "Блокировка ботов", "AI-анализ угроз"]
		},
		{
			icon: <FiZap className="w-8 h-8" />,
			title: "Ускорение сайтов",
			description: "Глобальная CDN сеть с интеллектуальным кэшированием",
			items: ["Глобальная CDN", "Автоматическое кэширование", "Автоматическое сжатие"]
		},
		{
			icon: <FiBarChart2 className="w-8 h-8" />,
			title: "Детальная аналитика",
			description: "Полная видимость трафика с AI-аналитикой в реальном времени",
			items: ["Графики в реальном времени", "Гео-аналитика", "AI-детекция аномалий"]
		},
		{
			icon: <FiSettings className="w-8 h-8" />,
			title: "Гибкая настройка",
			description: "Интуитивный интерфейс с тонкой настройкой защиты",
			items: ["Белые/черные списки", "Гео-фильтрация", "Защита от ботов", "Настройки интенсивности"]
		}
	];

	// В компоненте Home добавьте этот эффект
	useEffect(() => {
		if (!isLoadingPlans) {
			const hash = window.location.hash;
			if (hash === '#pricing' || hash === '#features') {
				setTimeout(() => {
					const element = document.getElementById(hash.replace('#', ''));
					if (element) {
						const yOffset = -80; // Отступ сверху для фиксированного header'а если есть
						const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

						window.scrollTo({
							top: y,
							behavior: 'smooth'
						});
					}
				}, 300);
			}
		}
	}, [isLoadingPlans]);

	const scrollToElement = useCallback((elementId) => {
		const element = document.getElementById(elementId);
		if (element) {
			const yOffset = -80; // Отступ сверху для учета возможного фиксированного header'а
			const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

			window.scrollTo({
				top: y,
				behavior: 'smooth'
			});
		}
	}, []);

	// Обработка якорных ссылок при загрузке страницы
	useEffect(() => {
		const handleHashScroll = () => {
			const hash = window.location.hash.replace('#', '');
			if (hash === 'pricing' || hash === 'features') {
				// Для pricing ждем загрузки данных, для features можно сразу
				if (hash === 'pricing' && isLoadingPlans) {
					return; // Ждем загрузки тарифов
				}

				setTimeout(() => {
					scrollToElement(hash);
				}, 100);
			}
		};

		handleHashScroll();
	}, [scrollToElement, isLoadingPlans]);

	// Обработка кликов по якорным ссылкам на той же странице
	useEffect(() => {
		const handleAnchorClick = (e) => {
			// Находим ближайшую ссылку
			const link = e.target.closest('a');
			if (link && link.hash) {
				const hash = link.hash.replace('#', '');

				// Проверяем, что это якорь на текущей странице
				if (hash === 'pricing' || hash === 'features') {
					e.preventDefault();

					// Для pricing ждем загрузки данных
					if (hash === 'pricing' && isLoadingPlans) {
						// Можно показать индикатор загрузки или просто подождать
						return;
					}

					scrollToElement(hash);

					// Обновляем URL без перезагрузки страницы
					window.history.pushState(null, null, link.hash);
				}
			}
		};

		// Добавляем обработчик ко всему документу
		document.addEventListener('click', handleAnchorClick);

		return () => {
			document.removeEventListener('click', handleAnchorClick);
		};
	}, [scrollToElement, isLoadingPlans]);

	// Обработка изменений hash в URL (если пользователь вручную меняет URL)
	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash.replace('#', '');
			if (hash === 'pricing' || hash === 'features') {
				if (hash === 'pricing' && isLoadingPlans) return;

				setTimeout(() => {
					scrollToElement(hash);
				}, 100);
			}
		};

		window.addEventListener('hashchange', handleHashChange);
		return () => {
			window.removeEventListener('hashchange', handleHashChange);
		};
	}, [scrollToElement, isLoadingPlans]);

	// Добавляем CSS для плавной прокрутки
	useEffect(() => {
		// Создаем элемент style для инъекции CSS
		const style = document.createElement('style');
		style.textContent = `
			html {
				scroll-behavior: smooth;
			}
			section {
				scroll-margin-top: 80px;
			}
		`;
		document.head.appendChild(style);

		return () => {
			document.head.removeChild(style);
		};
	}, []);

	return (
		<>
			<Seo
				title={`${serviceName} - Защита и ускорение сайтов`}
				description="Умная защита от DDoS атак, ботов и кибератак."
			/>


			<div className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
				<canvas
					ref={canvasRef}
					className="absolute inset-0 z-0"
					style={{ display: "block" }}
				/>

				<div className="absolute inset-0 z-0 bg-gradient-to-br from-blue-900/40 via-purple-900/30 to-black/50"></div>

				{/* Hero секция */}
				<section className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-4">
					<motion.h1
						className="text-5xl md:text-7xl lg:text-8xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 drop-shadow-2xl mb-6"
						initial={{ opacity: 0, y: -40 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 1 }}
					>
						{serviceName} Protection
					</motion.h1>

					<motion.p
						className="text-xl md:text-2xl lg:text-3xl max-w-4xl text-gray-200 mb-8 leading-relaxed"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 1 }}
					>
						Защита и ускорение вашего сайта с искусственным интеллектом
					</motion.p>

					<motion.p
						className="text-lg md:text-xl max-w-2xl text-gray-300 mb-12 leading-relaxed"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.6, duration: 1 }}
					>
						Полностью автоматизированная защита от кибератак с детальной аналитикой
						и молниеносным ускорением контента
					</motion.p>

					<motion.div
						className="flex flex-col sm:flex-row gap-4 items-center"
						initial={{ opacity: 0, y: 40 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.9, duration: 0.5 }}
					>
						<a
							href="/dashboard"
							className="px-8 py-4 flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-pink-500 rounded-2xl text-lg font-semibold shadow-2xl transition-all duration-300 hover:scale-105 group"
						>
							<FiSend className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
							Начать бесплатно (7 дней)
						</a>
					</motion.div>

					<motion.div
						className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 1.2, duration: 1 }}
					>
						<div>
							<div className="text-2xl md:text-3xl font-bold text-blue-400">99.9%</div>
							<div className="text-gray-400 text-sm">Аптайм</div>
						</div>
						<div>
							<div className="text-2xl md:text-3xl font-bold text-green-400">12ms</div>
							<div className="text-gray-400 text-sm">Скорость ответа</div>
						</div>
						<div>
							<div className="text-2xl md:text-3xl font-bold text-purple-400">2.1M+</div>
							<div className="text-gray-400 text-sm">Атак в день</div>
						</div>
						<div>
							<div className="text-2xl md:text-3xl font-bold text-red-400">100%</div>
							<div className="text-gray-400 text-sm">Успешных блокировок</div>
						</div>
					</motion.div>
				</section>

				{/* Карта кибератак в реальном времени */}
				<section className="relative z-10 py-20 bg-black/50 backdrop-blur-sm">
					<div className="max-w-7xl mx-auto px-4">
						<motion.div
							className="text-center mb-12"
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.8 }}
						>
							<h2 className="text-4xl md:text-5xl font-bold mb-4">
								Кибератаки в реальном времени
							</h2>
							<p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-4">
								По данным совместного исследования компаний <span className="font-semibold text-white"> WMX и Servicepipe</span>,
								за первое полугодие 2026 года было зафиксировано <span className="font-semibold text-white"> 849,7 млн веб-атак </span>
								на прикладной уровень сайтов и приложений российских компаний.
							</p>
							<p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-4">
								Это на <span className="font-semibold text-white"> 27% больше</span>, чем за аналогичный период 2025 года. При этом
								<span className="font-semibold text-white"> 44% атак </span> носили многоэтапный характер.
							</p>
							<p className="text-xl md:text-2xl font-semibold text-green-400 leading-relaxed">
								Сегодня защита сайта — не роскошь, а необходимость.
								Наш сервис надежно защищает ваши проекты от ботов, DDoS и других угроз,
								сохраняя доступность и безопасность вашего бизнеса.
							</p>
						</motion.div>

						<div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl p-8 border border-white/10">
							<GlobalStats />

							<div className="space-y-8">
								<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
									<h3 className="text-lg font-semibold mb-4">Карта мощных кибератак в реальном времени</h3>
									<div className="relative" style={{ height: "500px" }}>
										<img
											src="/map.svg"
											alt="World Map"
											className="hidden"
										/>
										<div
											className="absolute inset-0 z-0"
											style={{
												backgroundImage: "url('/map.svg')",
												backgroundSize: "contain",
												backgroundPosition: "center",
												backgroundRepeat: "no-repeat",
												opacity: 0.1
											}}
										/>
										<div className="absolute inset-0 z-10">
											<AttackMap />
										</div>
									</div>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
										<h3 className="text-lg font-semibold mb-4">Детализация DDoS атак за последний час</h3>
										<div className="h-64">
											<DDoSDetails />
										</div>
									</div>
									<div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 shadow-xl">
										<h3 className="text-lg font-semibold mb-4">Детализация атак на сайты за последний час</h3>
										<div className="h-64">
											<WebsiteAttacksDetails />
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Возможности системы */}
				<section id="features" className="relative z-10 py-20 bg-gradient-to-b from-gray-900/80 to-black/80">
					<div className="max-w-7xl mx-auto px-4">
						<motion.div
							className="text-center mb-16"
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.8 }}
						>
							<h2 className="text-4xl md:text-5xl font-bold mb-4">
								Полный контроль над безопасностью
							</h2>
							<p className="text-xl text-gray-300 max-w-3xl mx-auto">
								Мощный функционал для защиты любого веб-проекта — от личного блога
								до корпоративного портала с миллионами пользователей
							</p>
						</motion.div>

						<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
							{features.map((feature, index) => (
								<motion.div
									key={index}
									className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105"
									initial={{ opacity: 0, y: 40 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{ delay: index * 0.2, duration: 0.6 }}
								>
									<div className="flex items-center gap-4 mb-6">
										<div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl">
											{feature.icon}
										</div>
										<h3 className="text-2xl font-bold">{feature.title}</h3>
									</div>

									<p className="text-gray-300 mb-6 text-lg">{feature.description}</p>

									<ul className="space-y-3">
										{feature.items.map((item, idx) => (
											<li key={idx} className="flex items-center gap-3 text-gray-200">
												<FiCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
												{item}
											</li>
										))}
									</ul>
								</motion.div>
							))}
						</div>
					</div>
				</section>

				{/* Демонстрация дашборда */}
				<DashboardDemoSection serviceName={serviceName} />

				{/* Тарифы */}
				<PricingPlansSection plans={pricingPlans} isLoading={isLoadingPlans} />

				{/* CTA секция */}
				<section className="relative z-10 py-20">
					<div className="max-w-4xl mx-auto px-4 text-center">
						<motion.div
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.8 }}
						>
							<h2 className="text-4xl md:text-5xl font-bold mb-6">
								Готовы защитить свой сайт?
							</h2>
							<p className="text-xl text-gray-300 mb-8">
								Подключите надежную защиту от DDoS, ботов и кибератак — начните сегодня
							</p>

							<div className="flex flex-col sm:flex-row gap-4 justify-center">
								<a
									href="/dashboard"
									className="px-8 py-4 flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-pink-500 rounded-2xl text-lg font-semibold shadow-2xl transition-all duration-300 hover:scale-105 group"
								>
									<FiSend className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
									Начать бесплатно (7 дней)
								</a>
							</div>

							<p className="text-gray-400 text-sm mt-6">
								Быстрое подключение • Гибкая настройка • Удобная аналитика
							</p>
						</motion.div>
					</div>
				</section>
			</div>
		</>
	);
}
